/**
 * MCQAnswerInput Component
 * US-004 (FR-6.2 / COMP-001): Feeling-and-trait MCQ input for type_13 questions.
 *
 * - selectCount === 1 -> radio buttons (single choice; selecting replaces the prior pick).
 * - selectCount  >  1 -> checkboxes with an "{n} of {selectCount} selected" indicator
 *   (AC-004-3 / AC-6.2). Submit enabled only when EXACTLY selectCount are selected.
 * - Submit + all controls disabled while a learning-tip popup is open (isShowingTip, AC-004-6)
 *   and while submitting / waiting for tutor — mirrors TableAnswerInput's isDisabled gate (US-002).
 * - Submits the selected OPTION WORDS (never the A/B/C labels), newline-separated, in
 *   options-array order (AC-004-4 / AC-004-5 / AC-004-6).
 * - Touch targets: h-11 mobile / h-10 md, matching TableAnswerInput (NFR-6 / AC-004-7).
 * - Each option input carries aria-label={option} for accessibility (NFR-5 / AC-004-7).
 *
 * SECURITY (CCM-003): this component only ever receives the answer-free mcqSelect
 * { selectCount, options }. It NEVER receives correctAnswers or any answer-key field.
 * Grading remains server-side.
 *
 * KB TO-LL-044: reads camelCase fields (mcqSelect.selectCount, mcqSelect.options)
 * exactly as delivered by the API — no field-name aliasing at this boundary.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface MCQAnswerInputProps {
  /** Option strings from message.mcqSelect.options (validated metadata, NOT parsed from prose). */
  options: string[];
  /** Required number of selections from message.mcqSelect.selectCount (1 = single, >1 = multi). */
  selectCount: number;
  /** Callback fired on submit — receives the newline-joined selected option words. */
  onSubmit: (content: string) => void;
  /** True while a submission is in progress. */
  isSubmitting: boolean;
  /** True while waiting for the tutor to reply after submit. */
  isWaitingForTutor?: boolean;
  /** True while a learning-tip popup is open — disables every control (US-002 / AC-004-6). */
  isShowingTip?: boolean;
}

/**
 * MCQAnswerInput — radio/checkbox selection input for type_13 feeling-and-trait MCQ questions.
 *
 * Selection is stored as option INDICES (into `options`) so ordering by array position
 * and de-duplication are trivial, and radio "replace" is a one-liner.
 *
 * Implements US-004; enforces TO-LL-044 (camelCase API fields read at the boundary).
 */
export function MCQAnswerInput({
  options,
  selectCount,
  onSubmit,
  isSubmitting,
  isWaitingForTutor = false,
  // US-002 pattern: gate every control while a learning-tip popup is open (AC-004-6).
  isShowingTip = false,
}: MCQAnswerInputProps) {
  const isSingleSelect = selectCount === 1;

  // Selection stored as option INDICES (into `options`) so ordering + de-dupe are trivial.
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);

  // Same gate TableAnswerInput uses: disabled when submitting, waiting for tutor, or tip open.
  const isDisabled = isSubmitting || isWaitingForTutor || isShowingTip;

  // AC-004-3 / AC-6.2: Submit enabled ONLY when EXACTLY selectCount options are selected.
  const canSubmit = !isDisabled && selectedIndices.length === selectCount;

  const toggleOption = (index: number) => {
    if (isDisabled) return;
    setSelectedIndices((prev) => {
      if (isSingleSelect) {
        // Radio: replace the selection (AC-004-2).
        return [index];
      }
      // Checkbox: toggle membership.
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      }
      // Do not allow more than selectCount selections (keeps "n of N" indicator stable).
      if (prev.length >= selectCount) {
        return prev;
      }
      return [...prev, index];
    });
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    // AC-004-4 / AC-004-5: emit the OPTION WORDS, ordered by options-array position,
    // newline-separated. Single-select -> "worried"; multi -> "affectionate\nsupportive".
    // The word is submitted, never the A/B/C letter prefix (if any).
    const content = [...selectedIndices]
      .sort((a, b) => a - b)
      .map((i) => options[i])
      .join('\n');
    onSubmit(content);
  };

  return (
    <div className="border-t border-[#E7D8C8] bg-otter-paper p-3 md:p-4 sticky bottom-0 z-10 pb-[env(safe-area-inset-bottom)]">
      <div className="max-w-3xl mx-auto">
        {/* Multi-select count indicator (AC-004-3 / AC-6.2) */}
        {!isSingleSelect && (
          <p className="text-sm text-pebbles-500 text-center mb-2" aria-live="polite">
            {selectedIndices.length} of {selectCount} selected
          </p>
        )}

        {/* Options — scrollable list with max height (mirrors TableAnswerInput's scroll area) */}
        <div
          className="max-h-[180px] overflow-y-auto space-y-2 mb-2"
          role={isSingleSelect ? 'radiogroup' : 'group'}
          aria-label="Answer choices"
        >
          {options.map((option, index) => {
            const checked = selectedIndices.includes(index);
            return (
              <label
                key={`${index}-${option}`}
                className={cn(
                  'flex items-center gap-3 w-full h-11 md:h-10 px-3 rounded-lg border cursor-pointer select-none transition-colors',
                  'border-otter-sand bg-white',
                  checked && 'border-otter-orange-500 bg-otter-sand/40',
                  isDisabled && 'opacity-50 cursor-not-allowed',
                )}
              >
                <input
                  type={isSingleSelect ? 'radio' : 'checkbox'}
                  name="mcq-answer"
                  className="h-4 w-4 accent-otter-orange-500"
                  checked={checked}
                  disabled={isDisabled}
                  onChange={() => toggleOption(index)}
                  aria-label={option}
                />
                <span className="text-sm text-otter-earth-700">{option}</span>
              </label>
            );
          })}
        </div>

        {/* Waiting indicator (mirrors TableAnswerInput) */}
        {isWaitingForTutor && (
          <p className="text-sm text-pebbles-500 text-center mb-2">Waiting for tutor...</p>
        )}

        {/* Submit button: h-11 mobile / h-10 md — NFR-6 / AC-004-7 */}
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="w-full h-11 md:h-10"
          aria-label="Submit answer"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Sending
            </>
          ) : (
            'Submit Answer'
          )}
        </Button>
      </div>
    </div>
  );
}
